"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/design-system/utils/cn";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import * as React from "react";
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
interface CarouselProps {
  opts?: CarouselOptions;
  orientation?: "horizontal" | "vertical";
  plugins?: CarouselPlugin;
  setApi?: (api: CarouselApi) => void;
}
type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;
const CarouselContext = React.createContext<CarouselContextProps | null>(null);
function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
function Carousel(t0) {
  const $ = _c(47);
  let children;
  let className;
  let opts;
  let plugins;
  let props;
  let setApi;
  let t1;
  if ($[0] !== t0) {
    ({
      orientation: t1,
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = opts;
    $[4] = plugins;
    $[5] = props;
    $[6] = setApi;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    opts = $[3];
    plugins = $[4];
    props = $[5];
    setApi = $[6];
    t1 = $[7];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  const t2 = orientation === "horizontal" ? "x" : "y";
  let t3;
  if ($[8] !== opts || $[9] !== t2) {
    t3 = {
      ...opts,
      axis: t2
    };
    $[8] = opts;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const [carouselRef, carouselApi] = useEmblaCarousel(t3, plugins);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = api => {
      if (!api) {
        return;
      }
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const onSelect = t4;
  let t5;
  if ($[12] !== carouselApi) {
    t5 = () => {
      carouselApi?.scrollPrev();
    };
    $[12] = carouselApi;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const scrollPrev = t5;
  let t6;
  if ($[14] !== carouselApi) {
    t6 = () => {
      carouselApi?.scrollNext();
    };
    $[14] = carouselApi;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const scrollNext = t6;
  let t7;
  if ($[16] !== scrollNext || $[17] !== scrollPrev) {
    t7 = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      }
    };
    $[16] = scrollNext;
    $[17] = scrollPrev;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleKeyDown = t7;
  let t8;
  let t9;
  if ($[19] !== carouselApi || $[20] !== setApi) {
    t8 = () => {
      if (!(carouselApi && setApi)) {
        return;
      }
      setApi(carouselApi);
    };
    t9 = [carouselApi, setApi];
    $[19] = carouselApi;
    $[20] = setApi;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  React.useEffect(t8, t9);
  let t10;
  let t11;
  if ($[23] !== carouselApi) {
    t10 = () => {
      if (!carouselApi) {
        return;
      }
      onSelect(carouselApi);
      carouselApi.on("reInit", onSelect);
      carouselApi.on("select", onSelect);
      return () => {
        carouselApi?.off("select", onSelect);
      };
    };
    t11 = [carouselApi, onSelect];
    $[23] = carouselApi;
    $[24] = t10;
    $[25] = t11;
  } else {
    t10 = $[24];
    t11 = $[25];
  }
  React.useEffect(t10, t11);
  const hasAccessibleName = props["aria-label"] !== undefined || props["aria-labelledby"] !== undefined;
  const t12 = orientation || (opts?.axis === "y" ? "vertical" : "horizontal");
  let t13;
  if ($[26] !== canScrollNext || $[27] !== canScrollPrev || $[28] !== carouselApi || $[29] !== carouselRef || $[30] !== opts || $[31] !== scrollNext || $[32] !== scrollPrev || $[33] !== t12) {
    t13 = {
      carouselRef,
      api: carouselApi,
      opts,
      orientation: t12,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext
    };
    $[26] = canScrollNext;
    $[27] = canScrollPrev;
    $[28] = carouselApi;
    $[29] = carouselRef;
    $[30] = opts;
    $[31] = scrollNext;
    $[32] = scrollPrev;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = hasAccessibleName ? "carousel" : undefined;
  let t15;
  if ($[35] !== className) {
    t15 = cn("relative", className);
    $[35] = className;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const t16 = hasAccessibleName ? "region" : undefined;
  let t17;
  if ($[37] !== children || $[38] !== handleKeyDown || $[39] !== props || $[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
    t17 = <div aria-roledescription={t14} className={t15} data-slot="carousel" onKeyDownCapture={handleKeyDown} role={t16} {...props}>{children}</div>;
    $[37] = children;
    $[38] = handleKeyDown;
    $[39] = props;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t13 || $[45] !== t17) {
    t18 = <CarouselContext.Provider value={t13}>{t17}</CarouselContext.Provider>;
    $[44] = t13;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
}
function CarouselContent(t0) {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    carouselRef,
    orientation
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn("flex", t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2) {
    t3 = <div className={t2} {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== carouselRef || $[10] !== t3) {
    t4 = <div className="overflow-hidden" data-slot="carousel-content" ref={carouselRef}>{t3}</div>;
    $[9] = carouselRef;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function CarouselItem(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    orientation
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "pl-4" : "pt-4";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn("min-w-0 shrink-0 grow-0 basis-full", t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2) {
    t3 = <div aria-roledescription="slide" className={t2} data-slot="carousel-item" role="group" {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function CarouselPrevious(t0) {
  const $ = _c(19);
  let className;
  let icon;
  let iconClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      iconClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = icon;
    $[3] = iconClassName;
    $[4] = props;
  } else {
    className = $[1];
    icon = $[2];
    iconClassName = $[3];
    props = $[4];
  }
  const {
    orientation,
    scrollPrev,
    canScrollPrev
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("absolute size-8", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = !canScrollPrev;
  let t4;
  if ($[8] !== icon || $[9] !== iconClassName) {
    t4 = !icon && <ChevronLeft className={cn("h-1/2 w-1/2", iconClassName)} />;
    $[8] = icon;
    $[9] = iconClassName;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="sr-only">Previous slide</span>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== icon || $[13] !== props || $[14] !== scrollPrev || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t6 = <button className={t2} data-slot="carousel-previous" disabled={t3} onClick={scrollPrev} type="button" {...props}>{t4}{icon}{t5}</button>;
    $[12] = icon;
    $[13] = props;
    $[14] = scrollPrev;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function CarouselNext(t0) {
  const $ = _c(19);
  let className;
  let icon;
  let iconClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      iconClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = icon;
    $[3] = iconClassName;
    $[4] = props;
  } else {
    className = $[1];
    icon = $[2];
    iconClassName = $[3];
    props = $[4];
  }
  const {
    orientation,
    scrollNext,
    canScrollNext
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("absolute size-8", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = !canScrollNext;
  let t4;
  if ($[8] !== icon || $[9] !== iconClassName) {
    t4 = !icon && <ChevronRight className={cn("h-1/2 w-1/2", iconClassName)} />;
    $[8] = icon;
    $[9] = iconClassName;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="sr-only">Next slide</span>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== icon || $[13] !== props || $[14] !== scrollNext || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t6 = <button className={t2} data-slot="carousel-next" disabled={t3} onClick={scrollNext} type="button" {...props}>{t4}{icon}{t5}</button>;
    $[12] = icon;
    $[13] = props;
    $[14] = scrollNext;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
export { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, useCarousel };